import { useContext, useEffect, useMemo, useState } from 'react'
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router'
import { AlertTriangle, CheckCircle2, Play, Save, Undo2 } from 'lucide-react'
import {
  ConfirmDialog, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { formProblem } from '@/lib/api/fieldErrors'
import {
  useSettingsMutations, useWorkflowDefinitions, useWorkflowEntityKeys, useWorkflowPeople,
} from '../api'
import type { DryRunResult, WorkflowDefinitionView } from '../types'
import { FlowCanvas } from '../workflow-builder/FlowCanvas'
import { StepConfigPanel } from '../workflow-builder/StepConfigPanel'
import { StepPalette } from '../workflow-builder/StepPalette'
import {
  fromView, insertAt, locateError, move, newStep, toPayload,
} from '../workflow-builder/model'
import type { PaletteItem, StepDraft } from '../workflow-builder/model'

const TRIGGER = 'On submission'

interface Draft {
  /** The definition being edited, or null for one not saved yet. */
  sourceId: string | null
  name: string
  steps: StepDraft[]
}

function fingerprint(draft: Draft | null): string {
  return draft ? JSON.stringify({ name: draft.name, steps: toPayload(draft.steps) }) : ''
}

/** Blocks in-app navigation while there are unsaved changes. Only rendered under a data router. */
function NavigationGuard({ when }: { when: boolean }) {
  const blocker = useBlocker(when)
  return (
    <ConfirmDialog
      open={blocker.state === 'blocked'}
      onOpenChange={(open) => { if (!open && blocker.state === 'blocked') blocker.reset() }}
      title="Leave without saving?"
      description="The changes to this workflow have not been saved and will be lost."
      confirmLabel="Leave"
      destructive
      onConfirm={() => { if (blocker.state === 'blocked') blocker.proceed() }}
    />
  )
}

/**
 * Drawing an approval workflow, step by step, the way it will run.
 *
 * <p>Three columns: what can be added, the flow itself, and the selected step's settings. The
 * flow is one sequence — notifications, waits and conditions sit between approvals in the same
 * order the engine walks them — and a condition governs the steps below it up to the next one,
 * which the panel says in words because it is not a branching diagram and should not look like one.
 *
 * <p>Two things this screen still has to say out loud, as the list it replaces did. Editing a
 * live workflow saves a <b>new version</b>, left waiting until somebody makes it live, so the
 * requests already moving keep the chain they started on. And with nothing drawn the engine
 * approves at once — the "Default" status — which is deliberate, not broken.
 */
export function WorkflowsPage() {
  useBreadcrumb()
  const entityKeys = useWorkflowEntityKeys()
  const people = useWorkflowPeople()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const actions = useSettingsMutations()
  const dataRouter = useContext(UNSAFE_DataRouterContext)

  const [chosenKey, setChosenKey] = useState('')
  const entityKey = chosenKey
    || (entityKeys.data?.find((key) => key.key === 'leave.request') ?? entityKeys.data?.[0])?.key
    || ''
  const module = entityKeys.data?.find((key) => key.key === entityKey)
  const [scope, setScope] = useState('')
  const definitions = useWorkflowDefinitions(entityKey || undefined)

  const versions = useMemo(() => (definitions.data ?? [])
    .filter((row) => row.entityKey === entityKey && (row.legalEntityId ?? '') === scope)
    .sort((a, b) => b.versionNo - a.versionNo), [definitions.data, entityKey, scope])
  const [chosenVersion, setChosenVersion] = useState<string | null>(null)
  const selected: WorkflowDefinitionView | undefined = chosenVersion
    ? versions.find((row) => row.id === chosenVersion)
    : versions.find((row) => row.active) ?? versions[0]

  const [draft, setDraft] = useState<Draft | null>(null)
  const [baseline, setBaseline] = useState('')
  const [loadedFrom, setLoadedFrom] = useState('')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [stepErrors, setStepErrors] = useState<Record<string, Record<string, string>>>({})
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [pending, setPending] = useState<(() => void) | null>(null)
  const [dryRunOpen, setDryRunOpen] = useState(false)
  const [dryRunContext, setDryRunContext] = useState('')
  const [dryRun, setDryRun] = useState<DryRunResult | null>(null)
  const [dryRunError, setDryRunError] = useState<string | null>(null)

  const dirty = draft !== null && fingerprint(draft) !== baseline
  const source = `${entityKey}|${scope}|${selected?.id ?? 'new'}`

  function load(view: WorkflowDefinitionView | undefined) {
    const next: Draft = {
      sourceId: view?.id ?? null,
      name: view?.name ?? `${module?.label ?? 'Approval'} workflow`,
      steps: view ? [...view.steps].sort((a, b) => a.stepOrder - b.stepOrder).map(fromView) : [],
    }
    setDraft(next)
    setBaseline(fingerprint(next))
    setSelectedKey(next.steps[0]?.key ?? null)
    setStepErrors({})
    setError(null)
  }

  // A different module, scope or version on screen: load it. Done while rendering, the way React
  // documents adjusting state to a changed input, rather than in an effect that would paint the
  // old flow first. Unsaved changes were already confirmed away before the switch (see guard()).
  if (definitions.isSuccess && entityKey && loadedFrom !== source) {
    setLoadedFrom(source)
    load(selected)
  }

  useEffect(() => {
    if (!dirty) return undefined
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault() }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  /** Runs a switch straight away, or after "discard your changes?" when there are some. */
  function guard(action: () => void) {
    if (dirty) setPending(() => action)
    else action()
  }

  const steps = draft?.steps ?? []
  const setSteps = (next: StepDraft[]) => { if (draft) setDraft({ ...draft, steps: next }) }
  const selectedIndex = steps.findIndex((step) => step.key === selectedKey)
  const selectedStep = selectedIndex >= 0 ? steps[selectedIndex] : undefined
  const fieldLabel = (name: string) => module?.fields.find((field) => field.name === name)?.label ?? name
  const errorKeys = new Set(Object.keys(stepErrors))

  function add(item: PaletteItem, at = steps.length) {
    const step = newStep(item)
    setSteps(insertAt(steps, at, step))
    setSelectedKey(step.key)
  }

  async function save() {
    if (!draft) return
    setError(null)
    setNotice(null)
    setStepErrors({})
    const wasLive = selected?.active === true && draft.sourceId === selected.id
    try {
      const saved = await actions.saveWorkflow.mutateAsync({
        id: draft.sourceId ?? undefined,
        legalEntityId: scope || null,
        entityKey,
        name: draft.name.trim() || `${module?.label ?? 'Approval'} workflow`,
        description: selected?.description ?? null,
        autoApproveWhenEmpty: selected?.autoApproveWhenEmpty ?? true,
        steps: toPayload(draft.steps),
      })
      setChosenVersion(saved.id)
      setLoadedFrom(`${entityKey}|${scope}|${saved.id}`)
      load(saved)
      setNotice(wasLive
        ? `Saved as version ${saved.versionNo}. The live version is untouched until you make this one live.`
        : 'Saved.')
    } catch (caught) {
      const problem = formProblem(caught, 'That workflow could not be saved', { keepPath: true })
      const byStep: Record<string, Record<string, string>> = {}
      for (const [field, message] of Object.entries(problem.fields)) {
        const located = locateError(field)
        const step = located ? draft.steps[located.index] : undefined
        if (!located || !step) continue
        byStep[step.key] = { ...byStep[step.key], [located.part]: byStep[step.key]?.[located.part] ?? message }
      }
      setStepErrors(byStep)
      const first = draft.steps.find((step) => byStep[step.key])
      if (first) setSelectedKey(first.key)
      const count = Object.keys(byStep).length
      setError(count === 0 ? problem.summary
        : count === 1 ? 'One step needs attention — see the message on it.'
          : `${count} steps need attention — see the messages on them.`)
    }
  }

  const status = !selected ? 'DEFAULT' : selected.active ? 'ACTIVE' : 'DRAFT'

  return (
    <div className="space-y-4 pb-20">
      {dataRouter && <NavigationGuard when={dirty} />}
      <PageHeader
        title="Approval workflows"
        subtitle="Who is asked, who is told, and in what order — drawn the way it runs."
      />

      <Card className="flex flex-wrap items-end gap-3 p-3">
        <div className="min-w-48 flex-1">
          <Label htmlFor="wf-module">Module</Label>
          <Select
            id="wf-module" className="w-full" value={entityKey}
            onChange={(event) => {
              const next = event.target.value
              guard(() => { setChosenKey(next); setChosenVersion(null); setNotice(null) })
            }}
          >
            {entityKeys.data?.map((key) => (
              <option key={key.key} value={key.key}>{key.label}</option>
            ))}
          </Select>
        </div>
        <div className="min-w-48 flex-1">
          <Label htmlFor="wf-scope">Scope</Label>
          <Select
            id="wf-scope" className="w-full" value={scope}
            onChange={(event) => {
              const next = event.target.value
              guard(() => { setScope(next); setChosenVersion(null); setNotice(null) })
            }}
          >
            <option value="">Company-wide</option>
            {entities.data?.map((entity) => (
              <option key={entity.id} value={entity.id}>{entity.name}</option>
            ))}
          </Select>
        </div>
        {versions.length > 0 && (
          <div className="min-w-40">
            <Label htmlFor="wf-version">Version</Label>
            <Select
              id="wf-version" className="w-full" value={selected?.id ?? ''}
              onChange={(event) => {
                const next = event.target.value
                guard(() => { setChosenVersion(next); setNotice(null) })
              }}
            >
              {versions.map((row) => (
                <option key={row.id} value={row.id}>
                  v{row.versionNo}{row.active ? ' — live' : ''}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="flex items-center gap-2 pb-1">
          <StatusBadge
            status={status}
            tone={status === 'ACTIVE' ? 'success' : status === 'DRAFT' ? 'warning' : 'neutral'}
            label={status === 'ACTIVE' ? 'Active' : status === 'DRAFT' ? 'Draft' : 'Default'}
          />
          {selected && !selected.active && (
            <Button
              type="button" size="sm" disabled={dirty || actions.activateWorkflow.isPending}
              title={dirty ? 'Save your changes first' : undefined}
              onClick={() => {
                setError(null)
                actions.activateWorkflow.mutate(selected.id, {
                  onSuccess: () => setNotice(`Version ${selected.versionNo} is live. New requests use it from now on.`),
                  onError: (caught) => setError(toApiProblem(caught)?.detail ?? 'That could not be made live'),
                })
              }}
            >
              <CheckCircle2 className="size-4" aria-hidden="true" /> Make live
            </Button>
          )}
          {selected?.active && (
            <Button
              type="button" size="sm" variant="outline"
              onClick={() => actions.deactivateWorkflow.mutate(selected.id)}
            >
              Stop using it
            </Button>
          )}
          <Button
            type="button" size="sm" variant="outline" disabled={!selected || dirty}
            title={dirty ? 'Save your changes first' : undefined}
            onClick={() => { setDryRun(null); setDryRunError(null); setDryRunOpen(true) }}
          >
            <Play className="size-4" aria-hidden="true" /> Dry run
          </Button>
        </div>
      </Card>

      {status === 'DEFAULT' && definitions.isSuccess && (
        <p className="rounded-md border border-[var(--color-border)] bg-[var(--color-page)] p-3 text-sm text-[var(--color-body)]">
          Nothing is drawn for this module and scope, so the engine approves a request the moment it
          is raised. That is deliberate — a module works before anybody draws a workflow. Add steps
          and save to change it.
        </p>
      )}
      {selected?.editNote && (
        <p className="flex items-start gap-2 rounded-md border border-[var(--color-warning)] bg-[var(--color-tint-orange)] p-3 text-sm text-[var(--color-body)]">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--color-warning)]" aria-hidden="true" />
          {selected.editNote}
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-md border border-[var(--color-success)] bg-[var(--color-tint-green)] p-3 text-sm text-[var(--color-body)]">
          {notice}
        </p>
      )}

      {(entityKeys.isPending || definitions.isPending) && <Skeleton className="h-64" />}
      {definitions.isError && <ErrorState error={definitions.error} onRetry={() => void definitions.refetch()} />}

      {draft && definitions.isSuccess && (
        <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)] xl:grid-cols-[230px_minmax(0,1fr)_340px]">
          <StepPalette trigger={TRIGGER} onAdd={(item) => add(item)} />
          <FlowCanvas
            steps={steps}
            selectedKey={selectedKey}
            errorKeys={errorKeys}
            people={people.data}
            fieldLabel={fieldLabel}
            trigger={TRIGGER}
            onSelect={setSelectedKey}
            onInsert={(at, item) => add(item, at)}
            onMove={(at, by) => setSteps(move(steps, at, by))}
            onRemove={(at) => {
              const removed = steps[at]
              const next = steps.filter((_, index) => index !== at)
              setSteps(next)
              if (removed?.key === selectedKey) setSelectedKey(next[Math.min(at, next.length - 1)]?.key ?? null)
            }}
          />
          <div className="lg:col-span-2 xl:col-span-1">
            {selectedStep
              ? (
                <StepConfigPanel
                  key={selectedStep.key}
                  step={selectedStep}
                  index={selectedIndex}
                  count={steps.length}
                  people={people.data}
                  fields={module?.fields ?? []}
                  errors={stepErrors[selectedStep.key] ?? {}}
                  onChange={(changed) => setSteps(steps.map((step) => (step.key === changed.key ? changed : step)))}
                  onPrev={() => setSelectedKey(steps[selectedIndex - 1]?.key ?? selectedKey)}
                  onNext={() => setSelectedKey(steps[selectedIndex + 1]?.key ?? selectedKey)}
                  onClose={() => setSelectedKey(null)}
                />
              )
              : (
                <Card className="p-4 text-sm text-[var(--color-muted)]">
                  {steps.length === 0
                    ? 'Add a step from the palette, or with a + on the flow.'
                    : 'Select a step to configure it.'}
                </Card>
              )}
          </div>
        </div>
      )}

      {draft && (
        <div className="fixed inset-x-0 bottom-0 z-20 border-t border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 shadow-[0_-2px_8px_rgba(0,0,0,0.06)]">
          <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-end gap-3">
            {error && (
              <p role="alert" className="me-auto text-sm text-[var(--color-danger)]">{error}</p>
            )}
            {!error && (
              <p className="me-auto text-sm text-[var(--color-muted)]">
                {dirty ? 'Unsaved changes' : 'All changes saved'}
              </p>
            )}
            <Button
              type="button" variant="outline" disabled={!dirty}
              onClick={() => { load(selected); setNotice(null) }}
            >
              <Undo2 className="size-4" aria-hidden="true" /> Discard
            </Button>
            <Button
              type="button" disabled={!dirty || actions.saveWorkflow.isPending}
              onClick={() => { void save() }}
            >
              <Save className="size-4" aria-hidden="true" />
              {actions.saveWorkflow.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => { if (!open) setPending(null) }}
        title="Discard unsaved changes?"
        description="The changes to this workflow have not been saved. Switching now loses them."
        confirmLabel="Discard"
        destructive
        onConfirm={() => {
          const action = pending
          setPending(null)
          setBaseline(fingerprint(draft))
          action?.()
        }}
      />

      <FormDialog
        open={dryRunOpen}
        onOpenChange={setDryRunOpen}
        title={selected ? `Dry run · v${selected.versionNo}` : 'Dry run'}
        description="What the engine would do with a sample request — who it would ask and tell, what it would skip. Nothing is sent."
        submitLabel="Run"
        loading={actions.dryRun.isPending}
        error={dryRunError}
        onSubmit={() => {
          if (!selected) return
          setDryRunError(null)
          let context: object
          try {
            context = dryRunContext.trim() ? JSON.parse(dryRunContext) as object : {}
          } catch {
            setDryRun(null)
            setDryRunError('The sample is not valid JSON — for example {"days": 10}.')
            return
          }
          actions.dryRun.mutate(
            { id: selected.id, legalEntityId: scope || null, initiatorUserId: null, context },
            {
              onSuccess: setDryRun,
              onError: (caught) => setDryRunError(toApiProblem(caught)?.detail ?? 'The dry run failed'),
            })
        }}
      >
        <Label htmlFor="dry-run-context">Sample request, as JSON</Label>
        <Textarea
          id="dry-run-context" rows={3} value={dryRunContext} placeholder='{"days": 10}'
          onChange={(event) => setDryRunContext(event.target.value)}
        />
        {dryRun && (
          <div className="mt-3 space-y-2">
            <p className="text-sm text-[var(--color-body)]">{dryRun.outcome}</p>
            <ol className="space-y-1">
              {dryRun.steps.map((step) => (
                <li key={step.stepOrder} className="rounded-md border border-[var(--color-border)] p-2 text-sm">
                  <span className="font-medium text-[var(--color-heading)]">{step.stepOrder}. {step.name}</span>
                  {step.skipped && <span className="ms-2 text-xs text-[var(--color-muted)]">skipped</span>}
                  {(step.note ?? step.skipReason) && (
                    <span className="block text-xs text-[var(--color-muted)]">{step.note ?? step.skipReason}</span>
                  )}
                  {step.kind === 'APPROVAL' && step.approverNames.length > 0 && (
                    <span className="block text-xs text-[var(--color-success)]">{step.approverNames.join(', ')}</span>
                  )}
                  {step.approverNote && (
                    <span className="block text-xs text-[var(--color-danger)]">{step.approverNote}</span>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
